import {
  Button,
  ButtonsGroup,
  Checkbox,
  Flex,
  Modal,
  ModalProps,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { ReactElement, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUpdateOrganizationSettings, useUserOrganization } from '@modules-deprecated/app/organization'

import * as Styled from './styles'

interface ImportedInvoicesUploadInfoModalProps extends ModalProps {
  onAccept: () => void
  onDismiss: () => void
}

/**
 * STR-225 / S25 — informational interstitial shown before the first imported-invoice
 * upload, warning that uploads are forwarded to the advisor and cannot be undone.
 *
 * Only the primary button proceeds: the ✕, Escape and an overlay click all cancel the
 * upload the user initiated. Because those paths cancel rather than dismiss, the "don't
 * show this again" write only fires on the confirmed path — ticking the box and then
 * backing out would otherwise suppress the warning without ever confirming an upload.
 */
const ImportedInvoicesUploadInfoModalComponent = ({
  id,
  onAccept,
  onDismiss,
  ...modalProps
}: ImportedInvoicesUploadInfoModalProps): ReactElement => {
  const [shouldNotShowAgain, setShouldNotShowAgain] = useState(false)
  // `close()` fires every subscriber registered against the modal id, so the primary
  // button and the ✕ both land in the same `onClose`. This flag is what tells them apart.
  const hasConfirmedRef = useRef(false)
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { update: updateOrganizationSetting } = useUpdateOrganizationSettings()

  const organizationId = organization?.id

  const persistDismissalPreference = () => {
    if (!shouldNotShowAgain) {
      return
    }

    if (!organizationId) {
      trackError('"organizationId" has to be provided to dismiss the imported invoices upload info modal')

      return
    }

    // The write is optimistic (`useUpdateOrganizationSettings` patches the settings query
    // in `onMutate`), so the next upload attempt skips the dialog without a refetch.
    updateOrganizationSetting({
      key: 'isImportedInvoicesUploadInfoModalDismissed',
      organizationId,
      value: '1',
    })
  }

  const { close } = useModal(id, {
    onClose: () => {
      if (hasConfirmedRef.current) {
        persistDismissalPreference()
        onAccept()
      } else {
        onDismiss()
      }

      hasConfirmedRef.current = false
      // Reset on every close: `useComponentToggle` holds the conditional render for the exit
      // animation, so a fast re-open can land on this same instance and find the checkbox still
      // ticked. A cancelled close keeps the dialog in the user's future, so the box has to come
      // back unticked.
      setShouldNotShowAgain(false)
    },
  })

  const handleCheckboxChange = () => {
    setShouldNotShowAgain(!shouldNotShowAgain)
  }

  const handleConfirmButtonClick = () => {
    hasConfirmedRef.current = true
    close()
  }

  return (
    <Modal autofocusOnInput closable id={id} size="s" {...modalProps}>
      <Modal.Header title={t('imported_invoices.upload_info_modal.title')} />
      <Modal.Body>
        <Text>{t('imported_invoices.upload_info_modal.description')}</Text>
      </Modal.Body>
      <Modal.Footer>
        <Styled.FooterContentWrapper>
          <Flex alignItems="center" flexGrow={1} justifyContent="space-between">
            <Checkbox checked={shouldNotShowAgain} onChange={handleCheckboxChange}>
              <Text>{t('imported_invoices.upload_info_modal.dont_show_again')}</Text>
            </Checkbox>
            <ButtonsGroup>
              <Button onClick={handleConfirmButtonClick}>{t('imported_invoices.upload_info_modal.confirm')}</Button>
            </ButtonsGroup>
          </Flex>
        </Styled.FooterContentWrapper>
      </Modal.Footer>
    </Modal>
  )
}

export const ImportedInvoicesUploadInfoModal = withModalConditionalRender(ImportedInvoicesUploadInfoModalComponent)
